import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isWithinBounds,
  useGetFootprintingDairyHerdDetails,
  useMutateFootprintingDairyHerdDetails,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const HerdDetailsForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [avgCowLosses, setAvgCowLosses] = useState<number | null>(null);
  const [avgCowUsefulLifeMonths, setAvgCowUsefulLifeMonths] = useState<number | null>(null);

  const { data: herdDetails, isSuccess, isLoading } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });

  const { mutate: mutateHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      avgCowLosses,
      avgCowUsefulLifeMonths,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setAvgCowLosses(herdDetails.avgCowLosses ?? null);
      setAvgCowUsefulLifeMonths(herdDetails.avgCowUsefulLifeMonths ?? null);

      formRef.current?.setFieldsValue({
        avgCowLosses: herdDetails.avgCowLosses ?? null,
        avgCowUsefulLifeMonths: herdDetails.avgCowUsefulLifeMonths ?? null,
      });

      const fieldsToValidate = (["avgCowLosses", "avgCowUsefulLifeMonths"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef}>
      <Flex vertical gap={16}>
        <Card border className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("dairyHerdDetailsForm.cowLostQuestion")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("dairyHerdDetailsForm.cowLostQuestionDescription")}
              </Typography>
            </Flex>
            <InputNumber
              disabled={readOnly}
              name="avgCowLosses"
              dataTestId="footprinting-herd-cow-losses-input"
              label={t("dairyHerdDetailsForm.question.avgCowLossesInput")}
              suffix={t("dairyHerdDetailsForm.question.avgCowLossesSuffix")}
              value={avgCowLosses}
              initialValue={avgCowLosses}
              onChange={(v) => setAvgCowLosses(v)}
              validateFirst
              rules={[
                { required: true, type: "number", message: t("dairyHerdDetailsForm.error.validNumberCowLoss") },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("dairyHerdDetailsForm.error.validNumberCowLoss")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>

        <Card border className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("dairyHerdDetailsForm.avgCowUsefulLifeMonthsQuestion")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("dairyHerdDetailsForm.avgCowUsefulLifeMonthsQuestionDescription")}
              </Typography>
            </Flex>
            <InputNumber
              disabled={readOnly}
              name="avgCowUsefulLifeMonths"
              dataTestId="footprinting-herd-cow-useful-life-input"
              label={t("dairyHerdDetailsForm.question.avgCowUsefulLifeMonthsInput")}
              suffix={t("dairyHerdDetailsForm.question.avgCowUsefulLifeMonthsSuffix")}
              value={avgCowUsefulLifeMonths}
              initialValue={avgCowUsefulLifeMonths}
              onChange={(v) => setAvgCowUsefulLifeMonths(v)}
              validateFirst
              rules={[
                { required: true, type: "number", message: t("dairyHerdDetailsForm.error.validNumberMonths") },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isWithinBounds(v, 20, 150)) {
                      return Promise.reject(new Error(t("dairyHerdDetailsForm.error.validNumberMonths")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>
      </Flex>
    </Form>
  );
};

HerdDetailsForm.Key = "dairy.herdDetails";
export { HerdDetailsForm };
